/**
 * Shared Reckon Way content contracts.
 *
 * The application is JavaScript/Vue 2, so these JSDoc definitions provide the
 * schema without introducing a second build language.
 *
 * @typedef {'retreat'|'conference'|'holiday-party'|'local-event'} EventType
 * @typedef {'draft'|'coming-soon'|'published'|'archived'} EventStatus
 * @typedef {'place'|'restaurant'|'hotel'|'activity'|'session'|'venue'} ExperienceEntityType
 * @typedef {'published'|'draft'|'upcoming'} RecurringEventYearStatus
 *
 * @typedef {Object} MediaAsset
 * @property {string} id
 * @property {string} [src]
 * @property {string} [firebaseAssetId]
 * @property {string} alt
 * @property {string} [caption]
 * @property {string} [credit]
 * @property {number} [width]
 * @property {number} [height]
 * @property {'landscape'|'portrait'|'square'} [orientation]
 * @property {string} [position]
 * @property {boolean} [hiddenUntilPublished]
 *
 * @typedef {Object} StatItem
 * @property {string} id
 * @property {string} label
 * @property {string|number} value
 * @property {string} [icon]
 * @property {string} [suffix]
 *
 * @typedef {Object} ExperienceDNAItem
 * @property {string} id
 * @property {string} label
 * @property {number} score
 * @property {string} [icon]
 *
 * @typedef {Object} ExperienceEntity
 * @property {string} id
 * @property {string} slug
 * @property {ExperienceEntityType} entityType
 * @property {string} name
 * @property {string} [shortDescription]
 * @property {string} [fullDescription]
 * @property {string} [city]
 * @property {string} [region]
 * @property {string} [country]
 * @property {string} [address]
 * @property {string} [websiteUrl]
 * @property {string} [mapUrl]
 * @property {string} [instagramUrl]
 * @property {MediaAsset} [heroImage]
 * @property {MediaAsset[]} [gallery]
 * @property {string[]} [tags]
 *
 * @typedef {Object} EventEntityReference
 * @property {string} entityId
 * @property {number} [order]
 * @property {string} [date]
 * @property {string} [dayLabel]
 * @property {string} [titleOverride]
 * @property {string} [eventDescription]
 * @property {string} [whyWeWent]
 * @property {string} [whatHappened]
 * @property {string} [whatWeRemember]
 * @property {string} [recommendation]
 * @property {{heading: string, subheading?: string, date?: string, paragraphs: string[]}[]} [storySections]
 * @property {{slug: string, label: string, title: string, date: string, summary: string, sections: {heading: string, subheading?: string, date?: string, paragraphs: string[]}[]}[]} [storyPages]
 * @property {{label: string, storyPageSlug: string}[]} [storyLinks]
 * @property {MediaAsset} [cardImage]
 * @property {string} [chapterId]
 * @property {boolean} [featured]
 * @property {boolean} [hiddenUntilPublished]
 * @property {string[]} [relatedCrewIds]
 * @property {string[]} [relatedBadgeIds]
 * @property {string[]} [relatedMediaIds]
 *
 * @typedef {Object} StoryChapter
 * @property {string} id
 * @property {string} slug
 * @property {number} chapterNumber
 * @property {string} title
 * @property {string} [subtitle]
 * @property {string} [date]
 * @property {string} [dayLabel]
 * @property {string} [summary]
 * @property {string} [body]
 * @property {MediaAsset} [heroImage]
 * @property {MediaAsset[]} [gallery]
 * @property {'draft'|'scheduled'|'published'} status
 * @property {string} [publishedAt]
 * @property {EventEntityReference[]} [entityReferences]
 * @property {string[]} [badgeIds]
 *
 * @typedef {Object} ReckonEvent
 * @property {string} id
 * @property {string} slug
 * @property {string} title
 * @property {string} [subtitle]
 * @property {EventType} eventType
 * @property {EventStatus} status
 * @property {string} [startDate]
 * @property {string} [endDate]
 * @property {number} [year]
 * @property {string} [destination]
 * @property {string} [city]
 * @property {string} [region]
 * @property {string} [country]
 * @property {MediaAsset} [heroImage]
 * @property {string} [summary]
 * @property {StatItem[]} [atAGlance]
 * @property {StatItem[]} [tripByNumbers]
 * @property {ExperienceDNAItem[]} [experienceDNA]
 * @property {Object} [conference]
 * @property {Object} [holidayParty]
 * @property {Object} [localEvent]
 * @property {StoryChapter[]} [chapters]
 * @property {EventEntityReference[]} [places]
 * @property {EventEntityReference[]} [restaurants]
 * @property {EventEntityReference[]} [hotels]
 * @property {EventEntityReference[]} [activities]
 * @property {EventEntityReference[]} [sessions]
 * @property {EventEntityReference[]} [venues]
 * @property {Object[]} [crew]
 * @property {Object[]} [meritBadges]
 * @property {MediaAsset[]} [gallery]
 * @property {Object[]} [relatedEvents]
 * @property {Object[]} [tags]
 * @property {string} [closingReflection]
 * @property {boolean} [recurring]
 * @property {RecurringEventYear[]} [years]
 * @property {Object[]} [restaurantVisits]
 * @property {Object[]} [recurringCrew]
 * @property {Object[]} [traditions]
 */

/**
 * @typedef {Object} RecurringEventYear
 * @property {number} year
 * @property {string} [dates]
 * @property {string[]} attendees
 * @property {string[]} restaurants
 * @property {string[]} activities
 * @property {string[]} [highlights]
 * @property {MediaAsset[]} [gallery]
 * @property {RecurringEventYearStatus} [status]
 */

export const EVENT_TYPES = Object.freeze([
  'retreat',
  'conference',
  'holiday-party',
  'local-event',
]);

export const EVENT_STATUSES = Object.freeze([
  'draft',
  'coming-soon',
  'published',
  'archived',
]);

/**
 * Validate the small set of fields every layout relies on.
 * @param {ReckonEvent} event
 * @returns {ReckonEvent}
 */
export function defineReckonEvent(event) {
  if (!event || !event.id || !event.slug || !event.title) {
    throw new Error('A Reckon Way event requires id, slug, and title.');
  }

  if (!EVENT_TYPES.includes(event.eventType)) {
    throw new Error(`Unsupported Reckon Way event type: ${event.eventType}`);
  }

  if (!EVENT_STATUSES.includes(event.status)) {
    throw new Error(`Unsupported Reckon Way event status: ${event.status}`);
  }

  return event;
}
